import { useEffect, useMemo, useState } from 'react';

export type Timespan = 'DAILY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM';

export interface TimespanRange {
  from: string;
  to: string;
}

interface Props {
  value: Timespan;
  range: TimespanRange;
  onChange: (timespan: Timespan, range: TimespanRange) => void;
}

const PRESETS: { value: Timespan; label: string }[] = [
  { value: 'DAILY', label: 'Today' },
  { value: 'MONTHLY', label: 'This month' },
  { value: 'YEARLY', label: 'This year' },
  { value: 'CUSTOM', label: 'Custom' },
];

export function TimespanPicker({ value, range, onChange }: Props) {
  const [customFrom, setCustomFrom] = useState(toDateInput(range.from));
  const [customTo, setCustomTo] = useState(toDateInput(range.to));

  useEffect(() => {
    if (value !== 'CUSTOM') {
      setCustomFrom(toDateInput(range.from));
      setCustomTo(toDateInput(range.to));
    }
  }, [value, range.from, range.to]);

  const labelForRange = useMemo(() => formatRange(range), [range]);

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
        {PRESETS.map((preset) => {
          const active = preset.value === value;
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => {
                if (preset.value === 'CUSTOM') {
                  onChange('CUSTOM', range);
                } else {
                  onChange(preset.value, presetRange(preset.value));
                }
              }}
              className={
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors ' +
                (active
                  ? 'bg-primary-500 text-white'
                  : 'text-fg-muted hover:bg-surface-subtle hover:text-fg')
              }
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {value === 'CUSTOM' ? (
        <div className="flex items-center gap-2 text-xs text-fg-muted">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            onBlur={() => commitCustom(customFrom, customTo, onChange)}
            className="rounded-md border border-border bg-surface px-2 py-1 text-fg"
          />
          <span>—</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            onBlur={() => commitCustom(customFrom, customTo, onChange)}
            className="rounded-md border border-border bg-surface px-2 py-1 text-fg"
          />
        </div>
      ) : (
        <span className="text-xs text-fg-muted">{labelForRange}</span>
      )}
    </div>
  );
}

function commitCustom(
  fromInput: string,
  toInput: string,
  onChange: (timespan: Timespan, range: TimespanRange) => void,
) {
  if (!fromInput || !toInput) return;
  const from = new Date(`${fromInput}T00:00:00`);
  const to = new Date(`${toInput}T23:59:59`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return;
  onChange('CUSTOM', { from: from.toISOString(), to: to.toISOString() });
}

// eslint-disable-next-line react-refresh/only-export-components
export function presetRange(timespan: Timespan): TimespanRange {
  const now = new Date();
  if (timespan === 'DAILY') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: now.toISOString() };
  }
  if (timespan === 'MONTHLY') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return { from: start.toISOString(), to: now.toISOString() };
  }
  if (timespan === 'YEARLY') {
    const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
    return { from: start.toISOString(), to: now.toISOString() };
  }
  // Custom default = last 7 days
  const start = new Date(now);
  start.setDate(start.getDate() - 7);
  return { from: start.toISOString(), to: now.toISOString() };
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRange(range: TimespanRange): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  return `${fmt.format(new Date(range.from))} — ${fmt.format(new Date(range.to))}`;
}
